import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

/**
 * LT-15 — Job Type library hook.
 *
 * One fetch of `?include_archived=true`. `labelFor(key)` resolves
 * against ALL rows (including archived), so a tender using a job
 * type Chris later archived still renders its label — the label is
 * the user's own text; blanking it hides a data problem.
 *
 * `activeTypes` is a derived filter of the same list — not a second
 * request. Dropdowns should only offer types the user hasn't
 * archived yet, but the display-side helpers must still resolve
 * archived keys.
 *
 * `keyToLabel` is exposed for callers that only need the map
 * (e.g. a table cell rendered outside a component that already
 * accepts `labelFor` via props).
 *
 * `labelFor` falls back to the raw key on an unrecognised value
 * rather than to "" — an orphan tag should surface visibly, not
 * blank the cell.
 */
export function useJobTypeLibrary() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/settings/job-types?include_archived=true");
      const rows = Array.isArray(r.data) ? r.data.slice() : [];
      rows.sort((a, b) =>
        (a.display_order ?? 0) - (b.display_order ?? 0)
        || (a.label || "").localeCompare(b.label || "")
      );
      setTypes(rows);
    } catch (e) {
      setError(e);
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const keyToLabel = useMemo(() => {
    const m = {};
    for (const t of types) m[t.key] = t.label;
    return m;
  }, [types]);

  const labelFor = useCallback(
    (key) => {
      if (!key) return "";
      return keyToLabel[key] || key;
    },
    [keyToLabel],
  );

  const activeTypes = useMemo(
    () => types.filter((t) => !t.archived),
    [types],
  );

  return {
    types,
    activeTypes,
    labelFor,
    keyToLabel,
    loading,
    error,
    refetch: load,
  };
}
